import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { useInterests } from "@/hooks/usePortfolio";
import { SectionTitle } from "@/components/SectionTitle";

export const Interests = () => {
  const { data: interests, isLoading, error } = useInterests();

  if (isLoading || error || !interests) return null;

  const items = [...interests].sort((a,b) => a.displayOrder - b.displayOrder);

  return (
    <section id="interests" className="section-pad relative" data-testid="interests-section">
      <div className="container-x">
        <SectionTitle eyebrow="Beyond Work" title={<>Interests & <span className="italic gold-text">pursuits</span></>} subtitle="The pursuits that shape character beyond the boardroom." />

        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-6 items-stretch">
          {/* Featured panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
            className="lg:col-span-5 glass rounded-3xl grain p-8 md:p-10 relative overflow-hidden flex flex-col justify-between min-h-[420px]"
          >
            <div className="absolute -bottom-16 -left-16 w-72 h-72 rounded-full" style={{ background: "radial-gradient(closest-side, hsl(var(--gold)/0.18), transparent)" }} />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]">Passion · Discipline · Growth</div>
              <p className="mt-5 font-serif text-3xl md:text-4xl leading-[1.15]">
                The same focus that builds <span className="italic gold-text">enterprise sales pipelines</span> shapes life off the clock.
              </p>
              <p className="mt-5 text-sm text-muted-foreground text-justify-luxury max-w-md">
                Long-distance running for endurance. Cricket and Kabaddi for teamwork. Reading and public speaking for the mind. Travel and networking for perspective.
              </p>
            </div>
            <div className="relative mt-8 flex items-center gap-4">
              <div className="font-serif text-5xl md:text-6xl gold-text">{items.length}</div>
              <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground leading-tight">Personal<br/>Pursuits</div>
            </div>
          </motion.div>

          {/* Interest list — varied widths, full readability */}
          <div className="lg:col-span-7 grid grid-cols-12 gap-3 md:gap-4 content-start">
            {items.map((it, i) => {
              const rawIcon = it.iconUrl?.split(' ').pop()?.replace(/^fa-/, '') || 'heart';
              const iconNameCapitalized = rawIcon.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('');
              const Icon = Icons[iconNameCapitalized] || Icons[it.iconUrl] || Icons.Heart;
              
              // Varied column widths for visual rhythm — but enough room for descriptions
              const widths = [
                "col-span-12 sm:col-span-7",
                "col-span-12 sm:col-span-5",
                "col-span-12 sm:col-span-5",
                "col-span-12 sm:col-span-7",
                "col-span-12 sm:col-span-6",
                "col-span-12 sm:col-span-6",
                "col-span-12 sm:col-span-7",
                "col-span-12 sm:col-span-5",
                "col-span-12 sm:col-span-5",
                "col-span-12 sm:col-span-7",
              ];
              return (
                <motion.div
                  key={it.id}
                  initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.04 }}
                  data-testid={`interest-${it.id}`}
                  className={`${widths[i] || "col-span-6"} group relative glass rounded-2xl grain p-4 md:p-5 overflow-hidden hover:border-[hsl(var(--gold))]/50 transition-all hover:-translate-y-0.5 flex items-start gap-3.5`}
                >
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-[hsl(var(--gold))]/10 border border-[hsl(var(--gold))]/30 flex items-center justify-center group-hover:bg-[hsl(var(--gold))]/20 transition-colors">
                    <Icon className="w-5 h-5 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-base md:text-lg leading-tight">{it.interestName}</div>
                    <div className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{it.description}</div>
                  </div>
                  <div className="absolute top-2 right-3 font-serif text-[10px] tracking-wider text-[hsl(var(--gold))]/45">{String(i + 1).padStart(2, "0")}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
